import type { CSSProperties, MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import type { FeatureCategory } from '../types/feature';

type CustomSelectProps = {
	categories: Record<string, FeatureCategory>;
	value: string;
	setValue: (slug: string) => void;
	searchValue: string;
	clearSearchField: () => void;
};

const getCategoryStyle = (color: string): CSSProperties & { '--color': string } => ({ '--color': color });

export default function CustomSelect({ categories, value, setValue, searchValue, clearSearchField }: CustomSelectProps) {
	const [isOpen, setIsOpen] = useState(false);

	// Event listener to close the select when clicking outside
	useEffect(() => {
		const handleClickOutside = (e: globalThis.MouseEvent) => {
			if (!(e.target instanceof Element) || !e.target.closest('.feature__controls--select')) {
				setIsOpen(false);
			}
		};

		window.addEventListener('click', handleClickOutside);

		// Cleanup function to remove the event listener
		return () => {
			window.removeEventListener('click', handleClickOutside);
		};
	}, []);

	const handleOptionClick = (slug: string) => {
		if (searchValue !== '') {
			clearSearchField();
		}

		setValue(slug);
		setIsOpen(false);
	};

	const handleClearClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		handleOptionClick('all');
	};

	const getFeatureCount = (value: string) => {
		const allCount = categories.all.count;

		if (value === 'all') {
			return <span className="feature__controls--select-count">({allCount})</span>;
		} else {
			return (
				<span className="feature__controls--select-count">
					({categories[value].count} / {allCount})
				</span>
			);
		}
	};

	return (
		<div className="feature__controls--select">
			<div className={`feature__controls--select-trigger ${isOpen ? 'open' : 'closed'}`} onClick={() => setIsOpen(!isOpen)}>
				<div className="feature__controls--select-trigger-wrapper" style={getCategoryStyle(categories[value].color)}>
					{categories[value].illustration ? <span dangerouslySetInnerHTML={{ __html: categories[value].illustration }} /> : null}
					<span className="feature__controls--select-trigger-headline">{categories[value].title}</span> {getFeatureCount(value)}
				</div>

				<div className="feature__controls--select__controls">
					{value !== 'all' && <div className="feature__controls--select__controls-clear" onClick={handleClearClick}></div>}
					<div className="feature__controls--select__controls-arrow"></div>
				</div>
			</div>

			{isOpen && (
				<ul className="feature__controls--select-options">
					{Object.values(categories).map((category) => {
						return (
							<li
								key={category.slug}
								className="feature__controls--select-option"
								onClick={() => handleOptionClick(category.slug)}
								style={getCategoryStyle(category.color)}
							>
								{category.illustration ? <span dangerouslySetInnerHTML={{ __html: category.illustration }} /> : null}
								<span className="feature__controls--select-option-headline">
									{category.title} {getFeatureCount(category.slug)}
								</span>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
